import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Button, Spinner, Nav } from "react-bootstrap";
import OmrService from "../../_services/OmrService";
import AnswerKeyTab from "./components/AnswerKeyTab";
import UploadScansTab from "./components/UploadScansTab";
import ProcessReviewTab from "./components/ProcessReviewTab";
import ResultsTab from "./components/ResultsTab";

type TabKey = "setup" | "keys" | "upload" | "review" | "results";

const OmrTestDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const omrTestId = Number(id);

  const [test, setTest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>("setup");

  const loadTest = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await OmrService.getTest(omrTestId);
      setTest(resp.data?.data || null);
    } catch (err: any) {
      setError(err.message || "Failed to load test");
    } finally {
      setLoading(false);
    }
  }, [omrTestId]);

  useEffect(() => {
    loadTest();
  }, [loadTest]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: "center" }}><Spinner animation="border" size="sm" /> Loading...</div>;
  }

  if (!test) {
    return <div style={{ padding: 40, textAlign: "center", color: "var(--text-faint)" }}>Test not found.</div>;
  }

  const sectionsConfig = typeof test.SectionsConfig === "string"
    ? JSON.parse(test.SectionsConfig) : (test.SectionsConfig || []);

  const statusColor = {
    draft: "#6c757d", keys_entered: "#ffc107", scans_uploaded: "#17a2b8",
    processing: "#fd7e14", review: "#dc3545", finalized: "#28a745",
  }[test.Status as string] || "#6c757d";

  return (
    <div style={{ padding: "20px" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h4 style={{ margin: 0 }}>{test.Name}</h4>
          <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: 2 }}>
            {test.TestDate ? new Date(test.TestDate).toLocaleDateString() : "No date"} | {test.templateName || "Default template"} | {test.NumberOfSets} set(s)
          </div>
        </div>
        <span style={{ padding: "4px 12px", borderRadius: 4, fontSize: "0.8rem", fontWeight: "bold",
          background: statusColor + "22", color: statusColor, border: `1px solid ${statusColor}` }}>
          {test.Status}
        </span>
      </div>

      {error && (
        <div style={{ padding: "8px 12px", background: "var(--status-danger-bg)", color: "var(--status-danger-text)", borderRadius: 4, marginBottom: 12 }}>
          {error}
          <button onClick={() => setError(null)} style={{ float: "right", border: "none", background: "none", cursor: "pointer" }}>x</button>
        </div>
      )}

      {/* Tabs */}
      <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k as TabKey)} style={{ marginBottom: 16 }}>
        <Nav.Item><Nav.Link eventKey="setup">Setup</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="keys">Answer Keys</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="upload">Upload Scans</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="review">Process & Review</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="results">Results</Nav.Link></Nav.Item>
      </Nav>

      {/* Tab Content */}
      {activeTab === "setup" && (
        <SetupView test={test} sectionsConfig={sectionsConfig} onRefresh={loadTest} />
      )}
      {activeTab === "keys" && (
        <AnswerKeyTab omrTestId={omrTestId} numberOfSets={test.NumberOfSets} totalQuestions={test.TotalQuestions || 180} />
      )}
      {activeTab === "upload" && (
        <UploadScansTab omrTestId={omrTestId} onRefresh={loadTest} />
      )}
      {activeTab === "review" && (
        <ProcessReviewTab omrTestId={omrTestId} onRefresh={loadTest} />
      )}
      {activeTab === "results" && (
        <ResultsTab omrTestId={omrTestId} testName={test.Name} sectionsConfig={sectionsConfig} />
      )}
    </div>
  );
};

// ── Setup Tab (inline) ───────────────────────────────────────────────

const SetupView: React.FC<{ test: any; sectionsConfig: any[]; onRefresh: () => void }> = ({ test, sectionsConfig, onRefresh }) => {
  const [sections, setSections] = useState(sectionsConfig);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    try {
      setSaving(true);
      await OmrService.updateTest(test.Id, { sectionsConfig: sections });
      onRefresh();
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadSheet = async () => {
    try {
      const resp = await OmrService.generateSheetPdf(test.TemplateId || "default", {
        testName: test.Name,
        testDate: test.TestDate || "",
        institutionName: "RV Learning Hub",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `OMR_Sheet_${test.Name}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // ignore
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button variant="primary" size="sm" onClick={handleDownloadSheet}>
          Download OMR Sheet PDF
        </Button>
      </div>

      <h6>Sections Configuration</h6>
      <table className="table table-bordered table-sm" style={{ maxWidth: 600, fontSize: "0.85rem" }}>
        <thead>
          <tr style={{ background: "var(--surface-page)" }}>
            <th>Section Name</th>
            <th>Start Q</th>
            <th>End Q</th>
            <th>Marks/Q</th>
            <th>Neg. Marks</th>
          </tr>
        </thead>
        <tbody>
          {sections.map((s: any, i: number) => (
            <tr key={i}>
              <td>
                <input className="form-control form-control-sm" value={s.name}
                  onChange={(e) => {
                    const copy = [...sections];
                    copy[i] = { ...copy[i], name: e.target.value };
                    setSections(copy);
                  }} />
              </td>
              <td><input className="form-control form-control-sm" type="number" value={s.startQ}
                onChange={(e) => { const copy = [...sections]; copy[i] = { ...copy[i], startQ: Number(e.target.value) }; setSections(copy); }} /></td>
              <td><input className="form-control form-control-sm" type="number" value={s.endQ}
                onChange={(e) => { const copy = [...sections]; copy[i] = { ...copy[i], endQ: Number(e.target.value) }; setSections(copy); }} /></td>
              <td><input className="form-control form-control-sm" type="number" value={s.marksPerQ}
                onChange={(e) => { const copy = [...sections]; copy[i] = { ...copy[i], marksPerQ: Number(e.target.value) }; setSections(copy); }} /></td>
              <td><input className="form-control form-control-sm" type="number" value={s.negativeMarks}
                onChange={(e) => { const copy = [...sections]; copy[i] = { ...copy[i], negativeMarks: Number(e.target.value) }; setSections(copy); }} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
        {saving ? "Saving..." : "Save Configuration"}
      </Button>
    </div>
  );
};

export default OmrTestDetailPage;
